import { truncate } from "lodash";

import {
  DEFAULT_PDF_CSS_STYLE,
  normalizePrintableHtml
} from "components/pdf-generation-v3/pdf-generation-v3.utils";
import {
  RECIPIENT_DEFAULT_TYPE,
  RECIPIENT_PATIENT_OBJECT_TYPE,
  RECIPIENT_PHYSICIAN_OBJECT_TYPE
} from "constants/emails";
import { Clinic } from "services/types/clinics.type";
import type { Patient } from "services/types/patients.type";
import { type Physician } from "services/types/physician.type";
import {
  convertHtmlToText,
  formatPhoneNumber,
  getFullName
} from "utils/common";
import { EMAIL_REGEX } from "utils/validations.utils";

import { EmailFormValues } from "./email-form.component";
import { type Recipient } from "./recipients-field.component";

export const getNormalizedEmails = (value: (Recipient | string)[]) => {
  return value
    .filter((recipient) => {
      const email =
        typeof recipient === "string"
          ? recipient?.trim() || ""
          : recipient?.email?.trim() || "";
      return Boolean(email) && email.match(EMAIL_REGEX);
    })
    .filter((email, index) => index < 50);
};

export const formatRecipient = (
  value: Patient | Physician | null | undefined
) => {
  if (!value || !value.email) return null;

  if ("avatar" in value) {
    return {
      id: value.id,
      email: value.email!,
      name: getFullName({
        firstName: value?.first_name || "",
        middleName: value?.middle_name,
        preferredName: value?.preferred_name,
        lastName: value?.last_name || ""
      }),
      avatar: value?.photo,
      type: RECIPIENT_PATIENT_OBJECT_TYPE
    } as Recipient;
  } else {
    return {
      id: value.id!,
      email: value.email!,
      avatar: "",
      name: [value.first_name, value.last_name].filter(Boolean).join(" "),
      type: RECIPIENT_PHYSICIAN_OBJECT_TYPE
    } as Recipient;
  }
};

export const getDefaultSubject = (clinicName: string) =>
  `Message from ${clinicName}`;

export const getDefaultValueFromPatient = ({
  currentClinic,
  patient,
  userFullName,
  initialData,
  emailSignature,
  noSignature
}: {
  patient: Patient | undefined;
  currentClinic: Clinic;
  userFullName: string;
  initialData: Partial<EmailFormValues>;
  emailSignature?: string;
  noSignature?: boolean;
}) => {
  const clinicName = getDefaultEmailClinicName(currentClinic, patient);
  const { content, subject, ...restValues } = initialData;

  return {
    recipients: patient?.email
      ? [
          {
            id: patient.id,
            name:
              patient.fullName ||
              getFullName({
                firstName: patient.first_name,
                middleName: patient.middle_name,
                preferredName: patient.preferred_name,
                lastName: patient.last_name
              }),
            email: patient?.email,
            type: RECIPIENT_PATIENT_OBJECT_TYPE
          }
        ]
      : [],
    patient,
    subject: subject || getDefaultSubject(clinicName),
    content: getDefaultEmailContent(
      userFullName,
      content,
      emailSignature,
      noSignature
    ),
    ...restValues
  };
};

export const getDefaultEmailClinicName = (
  clinic?: Clinic,
  patient?: Patient
) => {
  return (
    patient?.preferred_clinic?.display_name ??
    clinic?.display_name ??
    clinic?.practice?.name ??
    ""
  );
};

export const getDefaultEmailContent = (
  userFullName: string,
  content?: string,
  emailSignature?: string,
  noSignature?: boolean
) => {
  if (noSignature) {
    return `<div>${content ? content : "&nbsp;"}</div>`;
  }
  return `<div>${content ? content : "&nbsp;"}</div><div>&nbsp;</div>${
    emailSignature
      ? `<div>${emailSignature}</div>`
      : `<div>Best regards,</div><div>${userFullName}</div>`
  }`;
};

export const getPatientPortalEmailContent = ({
  patientName,
  portalLink
}: {
  patientName: string;
  portalLink: string;
}): string =>
  `<div>Dear ${patientName},</div><div>&nbsp;</div><div>You can login to the <a href="${portalLink}">patient portal</a> to view all your unpaid invoices, and make payments.</div>`;

export const getStatementEmailContent = ({
  patientName
}: {
  patientName: string;
}): string =>
  `<div>Dear ${patientName},</div><div>&nbsp;</div><div>Please find your latest patient statement attached to this email for your review.</div><div>&nbsp;</div><div>If you have any questions regarding your statement, please contact our office directly.</div>`;

export const getInvoiceDefaultEmailContent = (params: {
  patientName: string;
  practiceName: string;
  clinicPhone: string;
  attachmentName?: string;
}) => {
  const {
    patientName,
    practiceName,
    clinicPhone,
    attachmentName = "invoice"
  } = params;

  const phone = `If you have any questions please call us at ${formatPhoneNumber(
    clinicPhone || "",
    "parenthesis"
  )}`;
  return `<div>Dear ${patientName}. <br />Please find attached your ${attachmentName} from ${practiceName}.<br />${
    clinicPhone ? phone : ""
  }</div><div>&nbsp;</div><div>Best regards,</div><div>${practiceName}</div>`;
};

/** Converts SUNO merge tag placeholders to {{tag}} so backend can replace with patient/clinic values */
export const normalizeMergeTagsForSend = (html: string): string => {
  return html
    .replaceAll(/<!--SUNO:(.*?)-->/g, "{{$1}}")
    .replaceAll(/!--SUNO:(.*?)--/g, "{{$1}}");
};

export const getEmailRequestBody = ({
  recipients,
  content,
  subject,
  files,
  patient,
  isUseBackendEmailTemplate,
  email_design_json
}: EmailFormValues & {
  practiceLogo: string | undefined;
  isTemplateUsed: boolean;
  isUseBackendEmailTemplate?: boolean;
  email_design_json?: any;
}) => {
  const normalizedRecipients = getNormalizedEmails(recipients);

  let html = isUseBackendEmailTemplate ? content : wrapWithBlankHtml(content);
  html = normalizeMergeTagsForSend(html);

  return {
    subject,
    patient: patient?.id,
    participants: normalizedRecipients.map((recipient) => ({
      type: RECIPIENT_DEFAULT_TYPE,
      object_id: typeof recipient === "string" ? undefined : recipient.id,
      address:
        typeof recipient === "string"
          ? recipient.trim()
          : recipient.email.trim(),
      object_type: typeof recipient === "string" ? undefined : recipient.type,
      name: typeof recipient === "string" ? undefined : recipient.name
    })),
    html,
    should_send: !files.length,
    mass_mailing: true,
    ...(email_design_json && { email_design_json })
  };
};

export const wrapWithBlankHtml = (content: string) => {
  return `<!DOCTYPE html> <html xmlns="http://www.w3.org/1999/xhtml"> <head> <meta http-equiv="Content-Type" content="text/html; charset=utf-8" /> <meta name="viewport" content="width=device-width" /><style>${DEFAULT_PDF_CSS_STYLE.replaceAll(
    "LatoRegular",
    "Helvetica, Arial, sans-serif"
  )} img { border: 0; height: auto; line-height: 100%; outline: none; text-decoration: none; -ms-interpolation-mode: bicubic; } </style></head><body>${getEmailPreviewString(
    content
  )} ${normalizePrintableHtml(content)}</body></html>`;
};

export const getEmailPreviewString = (content: string) => {
  return `<div style="display:none;font-size:1px;color:#ffffff;line-height:1px;max-height:0px;max-width:0px;opacity:0;overflow:hidden;margin-right:100%;">${truncate(
    convertHtmlToText(content),
    {
      separator: "…",
      length: 1024
    }
  )}</div>`;
};
